import React, { useState } from "react";
import styled from "styled-components";
import {
  Card,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  Icon,
  IconContainer,
  LegendCircle,
} from "../../components/visuals/styledComponents";
import { darkGreen } from "../../styles";
import ProgressBar from "react-bootstrap/ProgressBar";
import { SecurityTypeRatio } from "../../models/controlPanelDataModel";
import { graphPalette } from "../../styles";
import portfolioCompositionIcon from "../../assets/svg/portfolio_composition_money_icon.svg";

const StyledProgressBar = styled(ProgressBar)`
  width: 95%;
  height: 12px;
  cursor: pointer;
`;

const LegendContainer = styled.span`
  overflow-x: auto;
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const PortfolioComposition = ({
  controlPanelData,
  isLoading = false,
}: PortfolioCompositionProps): JSX.Element => {
  const [hoveredSecurityIndex, setHoveredSecurityIndex] = useState<number>(-1);
  controlPanelData = controlPanelData || [];
  // Sort the data by type_ratio
  controlPanelData.sort((a, b) => b.type_ratio - a.type_ratio);

  const adjustColor = (color: string, factor: number): string => {
    const colorVal = parseInt(color.slice(1), 16);
    const r = (colorVal >> 16) & 255;
    const g = (colorVal >> 8) & 255;
    const b = colorVal & 255;
    return `rgba(${r}, ${g}, ${b}, ${factor})`;
  };

  // Ensure there are enough colors by modifying existing ones if necessary
  const colors = graphPalette;
  while (colors.length < controlPanelData.length) {
    const baseColor = colors[colors.length % graphPalette.length];
    const newColor = adjustColor(
      baseColor,
      0.5 + (0.5 * (colors.length % graphPalette.length)) / graphPalette.length,
    );
    colors.push(newColor);
  }

  return (
    <Card h="120px" m="0 20px" isLoading={isLoading}>
      <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
        Portfolio Composition
      </CompassParagraph>
      <RowContainer jc="space-between" ai="center" mt={-12}>
        <StyledProgressBar>
          {controlPanelData.map((data, index) => (
            <ProgressBar
              variant="custom"
              now={data.type_ratio}
              key={index}
              style={{ backgroundColor: colors[index] }}
              onMouseEnter={(): void => setHoveredSecurityIndex(index)}
              onMouseLeave={(): void => setHoveredSecurityIndex(-1)}
            />
          ))}
        </StyledProgressBar>
        <IconContainer size={65} m={1} p={1}>
          <Icon src={portfolioCompositionIcon} />
        </IconContainer>
      </RowContainer>
      <RowContainer mt={-20} jc="flex-start" ai="center">
        <LegendContainer>
          {controlPanelData.map((data, index) => (
            <>
              <LegendCircle color={colors[index]}></LegendCircle>
              <CompassParagraph
                color={hoveredSecurityIndex === index ? darkGreen : "#8B909A"}
                fontSize={12}
                ws="nowrap"
              >{`${data.security_type} (${data.type_ratio}%)`}</CompassParagraph>
            </>
          ))}
        </LegendContainer>
      </RowContainer>
    </Card>
  );
};

type PortfolioCompositionProps = {
  controlPanelData?: SecurityTypeRatio[];
  isLoading?: boolean;
};
